import React, { useState, useEffect, useCallback } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames/bind';

// Utils
import {
  DETALIZATION_MODAL_GETTING_DETAIL_OPTIONS,
  DETALIZATION_MODAL_DOWNLOAD_OPTIONS,
  DETALIZATION_MODAL_COMMON,
} from 'constants/detalization';
import { BUTTON_CAPTION } from 'constants/common';

// Components
import {
  Modal,
  Text,
  Tabs,
  Button,
} from '@beef/ui-kit';
import { Icon } from 'components';
import { MODAL_NAMES } from 'constants/modal';
import { openPopup } from 'actions';
import { useIsMobileWidth } from 'utils/hooks/useIsMobile';
import { DatePicker, MailSection, SuccessSection } from './components';

// Actions
import { setDetalizationPageCurrentModal } from '../../store/actions';
import {
  downloadDetalizationReport,
  sendDetalizationReport,
  setDetalizationReportInitialState,
} from '../../store/detalizationReport/actions';

// Styles
import styles from './styles.pcss';

const cx = classNames.bind(styles);

const detailTabs = Object.keys(DETALIZATION_MODAL_GETTING_DETAIL_OPTIONS).map((key) => ({
  id: DETALIZATION_MODAL_GETTING_DETAIL_OPTIONS[key],
  title: DETALIZATION_MODAL_GETTING_DETAIL_OPTIONS[key],
}));

const downloadTabs = Object.keys(DETALIZATION_MODAL_DOWNLOAD_OPTIONS).map((key) => ({
  id: DETALIZATION_MODAL_DOWNLOAD_OPTIONS[key],
  title: DETALIZATION_MODAL_DOWNLOAD_OPTIONS[key],
  icon: <Icon name="letter" />,
}));

export const DetalizationReportModal = () => {
  const dispatch = useDispatch();

  const {
    isValid,
    startPeriod,
    endPeriod,
    email,
    isEmailExist,
    modalName,
    isError,
    isFetching,
    isReportSendingSuccess,
    successText,
    isEmailConfirmed,
  } = useSelector(({ application, detalizationNew }) => ({
    modalName: application.popup,
    isValid: detalizationNew.detalizationReport.isDataValid,
    startPeriod: detalizationNew.detalizationReport.startPeriod,
    endPeriod: detalizationNew.detalizationReport.endPeriod,
    isError: detalizationNew.detalizationReport.isError,
    isFetching: detalizationNew.detalizationReport.isFetching,
    isReportSendingSuccess: detalizationNew.detalizationReport.isFetchingSuccess,
    successText: detalizationNew.detalizationReport.successText,
    email: detalizationNew.personalData.emailAddress,
    isEmailExist: detalizationNew.personalData.isExist,
    isEmailConfirmed: detalizationNew.personalData.isEmailConfirmed,
    isUpdatingSuccess: detalizationNew.regularDetalizationReport.isUpdatingSuccess,
    isDataConfirmingSuccess: detalizationNew.personalData.isDataConfirmingSuccess,
  }), shallowEqual);

  const [selectedDetailTab, setSelectedDetailTab] = useState(DETALIZATION_MODAL_GETTING_DETAIL_OPTIONS.DOWNLOAD);
  const [selectedDownloadTab, setSelectedDownloadTab] = useState(DETALIZATION_MODAL_DOWNLOAD_OPTIONS.pdf);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(modalName === MODAL_NAMES.DETALIZATION_REPORT);
  }, [modalName]);

  const isMobile = useIsMobileWidth();

  const isDownloadTab = selectedDetailTab === DETALIZATION_MODAL_GETTING_DETAIL_OPTIONS.DOWNLOAD;
  const buttonCaption = isDownloadTab
    ? BUTTON_CAPTION.DOWNLOAD
    : BUTTON_CAPTION.SEND_ON_EMAIL;

  const onCloseModal = useCallback(() => {
    setSelectedDetailTab(DETALIZATION_MODAL_GETTING_DETAIL_OPTIONS.DOWNLOAD);
    dispatch(setDetalizationReportInitialState());
    dispatch(openPopup(null));
  }, [dispatch]);

  const onOpenEmailModal = useCallback(() => {
    dispatch(setDetalizationReportInitialState());
    setSelectedDetailTab(DETALIZATION_MODAL_GETTING_DETAIL_OPTIONS.DOWNLOAD);
    dispatch(openPopup(MODAL_NAMES.EMAIL));
  }, [dispatch]);

  const onOpenRegularDetalizationReportModal = useCallback(() => {
    dispatch(setDetalizationReportInitialState());
    setSelectedDetailTab(DETALIZATION_MODAL_GETTING_DETAIL_OPTIONS.DOWNLOAD);
    dispatch(openPopup(MODAL_NAMES.REGULAR_DETALIZATION_REPORT));
    dispatch(setDetalizationPageCurrentModal(MODAL_NAMES.REGULAR_DETALIZATION_REPORT));
  }, [dispatch]);

  useEffect(() => {
    const inValidPeriodData = !!startPeriod && !!endPeriod && isValid;

    const isDisabledButton = isDownloadTab ? !inValidPeriodData : !(inValidPeriodData && isEmailExist);

    setIsDisabled(isDisabledButton);
  }, [
    email,
    isValid,
    isDownloadTab,
    isEmailExist,
    startPeriod,
    endPeriod,
  ]);

  const onSaveReport = useCallback(
    (format) => () => {
      let selectedFormat = format;

      if (selectedFormat === DETALIZATION_MODAL_DOWNLOAD_OPTIONS.xls03) {
        selectedFormat = 'xls';
      }

      if (selectedFormat === DETALIZATION_MODAL_DOWNLOAD_OPTIONS.xls) {
        selectedFormat = 'xlsx';
      }

      const onClick = isDownloadTab ? downloadDetalizationReport : sendDetalizationReport;
      dispatch(onClick(selectedFormat));
    },
    [dispatch, selectedDownloadTab, isDownloadTab],
  );

  return (
    <Modal
      isOpen={isOpen}
      size={(isMobile || isReportSendingSuccess) ? 'xs' : 'm'}
      isCentered={isMobile || isReportSendingSuccess}
      onClose={onCloseModal}
    >
      {isReportSendingSuccess ? (
        <SuccessSection
          onClick={onCloseModal}
          successText={successText}
        />
      ) : (
        <div className={cx('content')}>
          <Text size="size3-b">{DETALIZATION_MODAL_COMMON.DETALIZATION_CAPTION}</Text>
          <div className={cx('tabs-wrapper')}>
            <Tabs
              noWrap
              onChange={setSelectedDetailTab}
              selectedTab={selectedDetailTab}
              tabs={detailTabs}
              variant="secondary"
            />
          </div>
          {!isDownloadTab && (
            <MailSection
              isConfirmed={isEmailConfirmed}
              email={email}
              onClick={onOpenEmailModal}
            />
          )}
          <DatePicker />
          <div className={cx('format-wrapper')}>
            <Text size="size5-b">Формат</Text>
            <div className={cx('format-tabs-wrapper')}>
              <Tabs
                noWrap
                onChange={setSelectedDownloadTab}
                selectedTab={selectedDownloadTab}
                tabs={downloadTabs}
                variant="secondary"
              />
              {isError && (
                <div className={cx('error')}>
                  <Icon name="error" className={cx('icon-error')} />
                  <Text color="scarlet" size="size5-r-l">{DETALIZATION_MODAL_COMMON.ERROR_TRY_AGAIN}</Text>
                </div>
              )}
            </div>
          </div>
          <div className={cx('control-wrapper')}>
            <div className={cx('control-save')}>
              <Button
                fullWidth={isMobile}
                icon={isDownloadTab && <Icon name="download" />}
                size="l"
                disabled={isDisabled}
                onClick={onSaveReport(selectedDownloadTab)}
                isLoading={isFetching}
              >
                {buttonCaption}
              </Button>
            </div>
            <Button
              fullWidth={isMobile}
              onClick={onOpenRegularDetalizationReportModal}
              variant="plain"
            >
              {BUTTON_CAPTION.SET_REGULAR_DETALIZATION_REPORT}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
